import {DatePicker, Form} from 'antd';
import {WeekPickerProps, RangePickerProps} from 'antd/es/date-picker';
import {FormInstance} from 'antd/es/form';
import moment, {Moment} from 'moment';
import React, {
    FC,
    useMemo,
    useState,
    MouseEvent,
    FocusEvent,
} from 'react';

import {ReactComponent as CloseXOutlined} from 'shared/assets/close-x-outlined.svg';
import {ReactComponent as LockIcon} from 'shared/assets/forms/lock.svg';
import {ReactComponent as CalendarIcon} from 'shared/assets/forms/time/calendar.svg';
import {
    DATE_DMY,
    DATE_FORMATS_PLACEHOLDERS_MAP,
    DATE_FOR_REQUEST,
    DATE_WITH_TIME_DOTS_SECONDS_24,
    DEFAULT_FORMAT_PLACEHOLDER,
} from 'shared/constants/date-format';
import {IFormItemProps} from 'shared/types';
import {getPopupContainer} from 'shared/utils';

import {EDateRangeInputSide} from './date-range-input-constants';
import s from './date-range-input.module.less';

interface IDateRangeInputProps extends IFormItemProps {
    showTime?: WeekPickerProps['showTime'];
    format?: string;
    formatToSet?: string;
    disabled?: boolean;
    fixedDropdown?: boolean;
    disabledDate?: (
        current: Moment,
        dateValues: [string | undefined, string | undefined],
        type: string,
        panelMode: [string, string],
    ) => boolean;
    dateRangeKeys: {from: string; to: string};
    dateRangeKey: string;
    form?: FormInstance;
    onChange?: (
        value: [string, string] | undefined,
        lastSelectedSide?: EDateRangeInputSide | undefined,
    ) => void;
}

export const DateRangeInput: FC<IDateRangeInputProps> = (
    {
        showTime,
        disabled,
        value,
        format,
        formatToSet = DATE_FOR_REQUEST,
        fixedDropdown = false,
        disabledDate,
        dateRangeKeys,
        dateRangeKey,
        form,
        onChange,
    }: IDateRangeInputProps,
) => {
    const [currentValues, setCurrentValues] = useState<[string | undefined, string | undefined]>(
        [undefined, undefined],
    );
    const [lastSelectedSide, setLastSelectedSide] = useState<EDateRangeInputSide | undefined>();
    const [panelMode, setPanelMode] = useState<[string, string]>(['', '']);

    const [defaultValue] = useState<RangePickerProps['value'] | undefined>(() => {
        if (!form) return undefined;

        const initialRangeData = form.getFieldsValue([dateRangeKeys.from, dateRangeKeys.to]);

        if (initialRangeData[dateRangeKeys.from] && initialRangeData[dateRangeKeys.to]) {
            const initialRangeDataFormatted = [
                moment(initialRangeData[dateRangeKeys.from]),
                moment(initialRangeData[dateRangeKeys.to]),
            ];

            if (!initialRangeDataFormatted[0].isValid() || !initialRangeDataFormatted[1].isValid()) return undefined;
            return initialRangeDataFormatted as RangePickerProps['value'];
        }
        return undefined;
    });

    const {RangePicker} = DatePicker;
    const defaultFormat = showTime ? DATE_WITH_TIME_DOTS_SECONDS_24 : DATE_DMY;

    const datePickerValue: any = (() => {
        if (!value) return undefined;

        const momentDate = [moment(value[0], formatToSet), moment(value[1], formatToSet)];

        if (!momentDate[0].isValid() || !momentDate[1].isValid()) return undefined;

        return momentDate;
    })();

    const placeholder = useMemo(() => {
        if (!format) return DEFAULT_FORMAT_PLACEHOLDER;

        if (DATE_FORMATS_PLACEHOLDERS_MAP.has(format)) {
            return DATE_FORMATS_PLACEHOLDERS_MAP.get(format);
        }

        return DEFAULT_FORMAT_PLACEHOLDER;
    }, [format]);

    const handleChange = (val: RangePickerProps['value']) => {
        if (!val) {
            form?.setFieldsValue({
                [dateRangeKeys.from]: undefined,
                [dateRangeKeys.to]: undefined,
                [dateRangeKey]: undefined,
            });
            onChange?.(undefined);
            return;
        }

        const momentDate = [moment(val[0] || ''), moment(val[1] || '')];
        const newValue = [momentDate[0].format(formatToSet), momentDate[1].format(formatToSet)];

        onChange?.(newValue as [string, string], lastSelectedSide);
        form?.setFieldsValue({
            [dateRangeKeys.from]: newValue[0],
            [dateRangeKeys.to]: newValue[1],
            [dateRangeKey]: undefined,
        });
    };

    const handleCalendarChange = (
        dates: [Moment, Moment], dateStrings: [string, string],
    ) => {
        setCurrentValues(dateStrings);
    };

    const handleDisableDate = (date: Moment) => {
        if (disabledDate) {
            return disabledDate(date, currentValues, lastSelectedSide!, panelMode);
        }
        return false;
    };

    const handleClickWrapper = (e: MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const {parentElement} = target;
        const pickerRangeElement = target.closest('.ant-picker-range') as HTMLElement;

        if (!pickerRangeElement) {
            return;
        }

        if (parentElement?.classList.contains('ant-picker-input')) {
            const prevElement = parentElement?.previousElementSibling;
            const isSecondInput = prevElement?.classList.contains('ant-picker-range-separator');
            setLastSelectedSide(isSecondInput ? EDateRangeInputSide.END : EDateRangeInputSide.START);
            return;
        }

        setLastSelectedSide(EDateRangeInputSide.START);
    };

    const handleBlurRangeInput = (e: FocusEvent<HTMLInputElement>) => {
        // значение currentValues не гарантирует что даты были установлены,
        // оно говорит только о том что была кликнута какая-то дата
        const isSomeDateWasClicked = currentValues[0] || currentValues[1];
        const isValueEmpty = !value;
        const isClickOutside = !e.target.value;

        // Если была кликнута какая-то дата, и не установлена вторая дата, при клике вне поля сбрасываем значение
        if (isSomeDateWasClicked && isValueEmpty && isClickOutside) {
            setCurrentValues([undefined, undefined]);
        }
    };

    const handlePanelChange = (_: any, modes: [string, string]) => {
        setPanelMode(modes);
    };

    return (
        <div
            onClick={handleClickWrapper}
        >
            <RangePicker
                disabled={disabled}
                disabledDate={handleDisableDate}
                value={datePickerValue}
                format={format ? [format] : [defaultFormat]}
                defaultValue={defaultValue}
                onChange={handleChange}
                onCalendarChange={handleCalendarChange as any}
                className={s['range-input']}
                suffixIcon={disabled ? <LockIcon /> : <CalendarIcon />}
                clearIcon={<CloseXOutlined className="clear-icon" />}
                placeholder={[placeholder || '', placeholder || '']}
                getPopupContainer={getPopupContainer(fixedDropdown)}
                onBlur={handleBlurRangeInput}
                onPanelChange={handlePanelChange}
            />
            <Form.Item
                name={dateRangeKeys.from}
                hidden
            >
                <></>
            </Form.Item>
            <Form.Item
                name={dateRangeKeys.to}
                hidden
            >
                <></>
            </Form.Item>
        </div>
    );
};
